/*
  Split media video slider — extracted from sections/split-media-video.liquid so it ships from a cacheable, deferred asset
  instead of inline markup. DOMContentLoaded-gated and guarded by .ks-initialized.
*/

document.addEventListener("DOMContentLoaded", () => {
  const sliderContainers = document.querySelectorAll("[data-video-slider]");
  sliderContainers.forEach((sliderContainer) => {
    if (!sliderContainer || sliderContainer.classList.contains("ks-initialized")) return;

    // Scope all controls to this container only
    const prevBtn = sliderContainer.querySelector(".nav-prev");
    const nextBtn = sliderContainer.querySelector(".nav-next");
    const dotsContainer = sliderContainer.querySelector(".hero-dots");
    const toggleBtn = sliderContainer.querySelector(".nav-toggle");

    function navigationPlugin({ dots, prev, next } = {}) {
      return (slider) => {
        let dotButtons = [];
        let cleanupFns = [];

        function onPrev() { slider.prev(); }
        function onNext() { slider.next(); }

        function setupArrows() {
          if (prev) {
            prev.addEventListener("click", onPrev);
            cleanupFns.push(() => prev.removeEventListener("click", onPrev));
          }
          if (next) {
            next.addEventListener("click", onNext);
            cleanupFns.push(() => next.removeEventListener("click", onNext));
          }
          updateArrowState();
        }

        function updateArrowState() {
          // arrows never disabled on loop
          if (!prev && !next) return;
          const isLoop = !!slider.options.loop;
          if (isLoop) {
            [prev, next].forEach((el) => {
              if (!el) return;
              el.classList.remove("arrow--disabled");
              el.removeAttribute("aria-disabled");
              el.tabIndex = 0;
            });
            return;
          }

          const rel = slider.track.details.rel;
          const last = slider.track.details.slides.length - 1;

          const setDisabled = (el, disabled) => {
            if (!el) return;
            el.classList.toggle("arrow--disabled", disabled);
            el.setAttribute("aria-disabled", String(disabled));
            el.tabIndex = disabled ? -1 : 0;
          };

          setDisabled(prev, rel === 0);
          setDisabled(next, rel === last);
        }

        function buildDots() {
          if (!dots) return;
          // clear to avoid duplicating on optionsChanged
          dots.innerHTML = "";
          dots.setAttribute("role", "tablist");

          const slidesCount = slider.track.details.slides.length;
          dotButtons = Array.from({ length: slidesCount }, (_, idx) => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "dot";
            btn.setAttribute("role", "tab");
            btn.setAttribute("aria-label", `Go to slide ${idx + 1}`);
            btn.addEventListener("click", () => slider.moveToIdx(idx));
            dots.appendChild(btn);
            cleanupFns.push(() => btn.remove());
            return btn;
          });

          updateDots();
        }

        function updateDots() {
          if (!dotButtons.length) return;
          const active = slider.track.details.rel;
          dotButtons.forEach((btn, idx) => {
            const on = idx === active;
            btn.classList.toggle("dot--active", on);
            if (on) btn.setAttribute("aria-current", "true");
            else btn.removeAttribute("aria-current");
          });
        }

        slider.on("created", () => {
          setupArrows();
          buildDots();
          updateDots();
          sliderContainer.classList.add("ks-initialized");
        });

        slider.on("slideChanged", () => {
          updateDots();
          updateArrowState();
        });

        slider.on("optionsChanged", () => {
          buildDots();
          updateArrowState();
        });

        slider.on("destroyed", () => {
          cleanupFns.forEach((fn) => fn());
          cleanupFns = [];
          dotButtons = [];
          if (dots) dots.innerHTML = "";
          sliderContainer.classList.remove("ks-initialized");
        });
      };
    }

    // autoplay plugin with play/pause button (scoped to this container)
    function autoplayPlugin({ interval = 3000, toggleButton } = {}) {
      return (slider) => {
        let timeoutId;
        let playing = true;
        let onToggleClick;

        function clearTimer() {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
          }
        }

        function queueNext() {
          clearTimer();
          if (!playing) return;
          timeoutId = setTimeout(() => {
            slider.next();
          }, interval);
        }

        function updateButton() {
          if (!toggleButton) return;
          toggleButton.dataset.state = playing ? "playing" : "paused";
          toggleButton.setAttribute("aria-pressed", playing ? "true" : "false");
          toggleButton.setAttribute("aria-label", playing ? "Pause slideshow" : "Play slideshow");
        }

        function play() {
          if (playing) return;
          playing = true;
          updateButton();
          queueNext();
        }

        function pause() {
          if (!playing) return;
          playing = false;
          updateButton();
          clearTimer();
        }

        slider.on("created", () => {
          updateButton();
          queueNext();

          if (toggleButton) {
            onToggleClick = () => (playing ? pause() : play());
            toggleButton.addEventListener("click", onToggleClick);
          }
        });

        // Clear while user interacts; re-queue after animations if still playing
        slider.on("dragStarted", clearTimer);
        slider.on("animationEnded", queueNext);
        slider.on("updated", queueNext);

        slider.on("destroyed", () => {
          clearTimer();
          if (toggleButton && onToggleClick) {
            toggleButton.removeEventListener("click", onToggleClick);
          }
        });

        slider.autoplay = { play, pause, isPlaying: () => playing };
      };
    }

    const slideCount = sliderContainer.querySelectorAll('.keen-slider__slide').length;
    const multiSlides = slideCount > 1;

    const heroSlider = new KeenSlider(
      sliderContainer,
      {
        loop: multiSlides,
        slides: { perView: 1, spacing: 8 },
        rubberband: true,
        renderMode: "performance",
      },
      [
        navigationPlugin({ dots: dotsContainer, prev: prevBtn, next: nextBtn }),
        autoplayPlugin({ interval: 4000, toggleButton: toggleBtn })
      ]
    );
  });

  function ratioValueMap(value) {
    switch (value) {
      case "16-9":
        return "16 / 9";
      case "4-3":
        return "4 / 3";
      case "1-1":
        return "1 / 1";
      case "4-5":
        return "4 / 5";
      case "9-16":
        return "9 / 16";
      default:
        return "";
    }
  }

  function applyVideoRatios() {
    const isMobile = window.matchMedia("(max-width: 767.98px)").matches;

    document.querySelectorAll(".split-media-video[data-section-type='video']").forEach((section) => {
      const ratioDesktop = section.dataset.videoRatioDesktop || "16-9";
      const ratioMobile = section.dataset.videoRatioMobile || "default";
      const videoBoxes = section.querySelectorAll(".custom-video-ratio");

      if (!videoBoxes.length) return;

      let ratio = ratioValueMap(ratioDesktop);

      if (isMobile) {
        ratio = ratioMobile === "default"
          ? ratioValueMap("16-9")
          : ratioValueMap(ratioMobile);
      }

      videoBoxes.forEach((videoBox) => {
        if (ratio) {
          videoBox.style.setProperty("--video-aspect-ratio", ratio);
          videoBox.classList.add("has-fixed-ratio");
        } else {
          videoBox.style.removeProperty("--video-aspect-ratio");
          videoBox.classList.remove("has-fixed-ratio");
        }
      });
    });
  }

  applyVideoRatios();

  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(applyVideoRatios, 100);
  });
});

// Scope the sizes attribute update to each slider instance
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-video-slider]').forEach((el) => {
    el.querySelectorAll('img').forEach(img => {
      // half viewport on desktop, full viewport on mobile:
      img.setAttribute('sizes', '(min-width: 990px) 50vw, 100vw');
    });
  });
});
